import React, { useContext, useEffect, useRef, useState } from 'react';

import { CommentListResult } from '../../../../api-client/models/project/common/CommentListResult';
import { createCommentManager } from '../../../../di';
import { useErrorCallback } from '../../../../hooks';
import { PaginationLoadMore } from '../../../pagination';
import Comment from '../Comment';
import CommentForm from '../CommentForm';
import { DetailedProjectContext } from '../DetailedProjectContext/DetailedProjectContext';
import DetailedProjectNavigation from '../DetailedProjectNavigation';
import styles from './ProjectChat.module.scss';

const INITIAL_PAGE = 1;
const COMMENTS_PER_PAGE = 5;

export const ProjectChat = () => {
  const project = useContext(DetailedProjectContext).project;
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const handleError = useErrorCallback();
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [commentListResult, setCommentListResult] = useState<CommentListResult>({
    comments: [],
    totalCount: 0,
    chatAvailable: false,
  });

  const loadData = async (pageToLoad: number, perPage: number, append = false) => {
    setLoading(true);
    try {
      const provider = await createCommentManager();
      const nextResult = await provider.getList(project.uuid, pageToLoad, perPage);

      setCommentListResult((prev) => {
        if (!append || pageToLoad === INITIAL_PAGE) {
          return nextResult;
        }

        return {
          ...nextResult,
          comments: [...prev.comments, ...nextResult.comments],
        };
      });
    } catch (err: any) {
      await handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(INITIAL_PAGE, COMMENTS_PER_PAGE, false);
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      setTimeout(() => {
        chatContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, []);

  const handleCommentCreated = () => {
    setPage(INITIAL_PAGE);
    loadData(INITIAL_PAGE, COMMENTS_PER_PAGE, false);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadData(nextPage, COMMENTS_PER_PAGE, true);
  };

  const handleReset = () => {
    setPage(INITIAL_PAGE);
    loadData(INITIAL_PAGE, COMMENTS_PER_PAGE, false);
  };

  return (
    <div className="project-chat-container" ref={chatContainerRef}>
      <div className="detailed-project__header">
        <DetailedProjectNavigation />
      </div>
      <div className="detailed-project__content">
        <div className={styles.projectChat__commentbox}>
          {commentListResult.chatAvailable && (
            <CommentForm projectUuid={project.uuid} onCreated={handleCommentCreated} />
          )}
          <ul className={styles.projectChat__list}>
            {commentListResult.comments.map((comment) => (
              <Comment
                comment={comment}
                projectUuid={project.uuid}
                key={comment.uuid}
                onCommentCreated={handleCommentCreated}
                chatAvailable={commentListResult.chatAvailable}
              />
            ))}
          </ul>
          <PaginationLoadMore
            loaded={commentListResult.comments.length}
            total={commentListResult.totalCount}
            page={page}
            loading={loading}
            onLoadMore={handleLoadMore}
            onReset={handleReset}
          />
        </div>
      </div>
    </div>
  );
};
